import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TAppRootState } from '@store/reducer';
import { TAppDispatch } from '@store/store';

import { uuid } from '@utils/common';

import { ISnackbarProps } from '@interfaces/notifications/snackbar';

import { TBrowser, TSnackbarTypes } from '@enums/common';

const SNACKBAR_DELAY = 6000;

interface IInitialState {
	browser: TBrowser | null;
	isLoading: boolean;
	snackbars: Array<ISnackbarProps>;
	isMobileDevice: boolean;
	isKaiAssistantLoaded?: boolean;
	isMapMode: boolean;
	isAdminSidebarOpen: boolean;
	isProductionIdams?: boolean;
}

export const initialState: IInitialState = {
	browser: null,
	isLoading: false,
	snackbars: [],
	isMobileDevice: false,
	isKaiAssistantLoaded: false,
	isMapMode: false,
	isAdminSidebarOpen: false,
};

const common = createSlice({
	name: 'common',
	initialState,
	reducers: {
		setBrowser: (state, action: PayloadAction<{
			browser: TBrowser | null;
			isMobileDevice: boolean;
		}>) => {
			state.browser = action.payload.browser;
			state.isMobileDevice = action.payload.isMobileDevice;
		},
		removeSnackbar: (state, action: PayloadAction<{ id: string }>) => {
			state.snackbars = state.snackbars.filter(snackbar => snackbar.id !== action.payload.id);
		},
		addSnackbar: (state, action: PayloadAction<ISnackbarProps>) => {
			state.snackbars.push(action.payload);
		},
		setIsKaiAssistantLoaded: (state, action: PayloadAction<boolean>) => {
			state.isKaiAssistantLoaded = action.payload;
		},
		setIsMapMode: (state, action: PayloadAction<boolean>) => {
			state.isMapMode = action.payload;
		},
		setIsAdminSidebarOpen: (state, action: PayloadAction<boolean>) => {
			state.isAdminSidebarOpen = action.payload;
		},
		setIsProductionIdams: (state, action: PayloadAction<boolean>) => {
			state.isProductionIdams = action.payload;
		},
	},
});

export const setSnackbar = ({
	message,
	type,
	delay = SNACKBAR_DELAY,
}: {
	message: string;
	type: TSnackbarTypes;
	delay?: number;
}) => (dispatch: TAppDispatch, getState: () => TAppRootState) => {
	const id = uuid();
	const { common: { snackbars } } = getState();
	const existingSnackbar = snackbars.find((snackbar) => snackbar.type === type && snackbar.message === message);

	if (existingSnackbar) return;

	dispatch(addSnackbar({ message, type, id }));

	setTimeout(() => {
		dispatch(removeSnackbar({ id }));
	}, delay);
};

export const {
	setBrowser,
	removeSnackbar,
	addSnackbar,
	setIsKaiAssistantLoaded,
	setIsMapMode,
	setIsAdminSidebarOpen,
	setIsProductionIdams,
} = common.actions;

export default common.reducer;
